/**
 * Venue details edit section for the venue page Details tab: name, region,
 * domain and description. Reads as a record and edits in place.
 */
import { useViewport } from '../../../../hooks/useViewport';
import { useWritableRegions } from '../../primitives/use-writable-regions';
import { Field } from '../venueCreateFields';
import { VenueRegionField } from '../VenueRegionField';

import { descriptionEditedBy } from './booking-content-audit';
import { DescriptionField } from './DescriptionField';
import { DescriptionView } from './DescriptionView';
import { EditSection } from './EditSection';
import { Input } from './Input';
import { ReadGrid } from './ReadGrid';
import { grid2, nullable, type SectionProps } from './section-props';
import { useFormDraft } from './use-form-draft';

export function IdentitySection({ venue, readOnly, save }: SectionProps) {
  const seed = {
    name: venue.name,
    domain: venue.domain ?? '',
    description: venue.description ?? '',
    regionId: venue.regionId ?? '',
  };
  const { draft, setField, reset } = useFormDraft(seed, venue.updatedAt);
  const writable = useWritableRegions();
  const regionName = venue.region?.name ?? 'Unassigned';
  const regionChanged = draft.regionId !== seed.regionId;
  const { isPhone } = useViewport();
  const editedBy = descriptionEditedBy(venue.audit);

  return (
    <EditSection
      first
      title="Venue details"
      hint="public-facing"
      readOnly={readOnly}
      onCancel={reset}
      onSave={() =>
        save({
          name: draft.name.trim(),
          domain: nullable(draft.domain),
          description: nullable(draft.description),
          ...(regionChanged ? { regionId: draft.regionId || null } : {}),
        })
      }
      view={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ReadGrid
            cols={isPhone ? 2 : 3}
            rows={[
              { label: 'Venue name', value: venue.name },
              { label: 'Region', value: regionName },
              { label: 'Domain', value: venue.domain, mono: true, full: isPhone },
            ]}
          />
          <DescriptionView
            description={venue.description}
            source={venue.descriptionSource}
            editedBy={editedBy}
          />
        </div>
      }
      form={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={grid2}>
            <Field label="Venue name" required>
              <Input value={draft.name} onChange={(v) => setField('name', v)} />
            </Field>
            <VenueRegionField
              regions={writable.regions}
              loading={writable.loading}
              failed={writable.failed}
              value={draft.regionId}
              onChange={(id) => setField('regionId', id)}
            />
          </div>
          <Field label="Domain" required>
            <Input mono value={draft.domain} onChange={(v) => setField('domain', v)} />
          </Field>
          <DescriptionField
            value={draft.description}
            onChange={(v) => setField('description', v)}
            source={venue.descriptionSource}
            editedBy={editedBy}
            rows={isPhone ? 7 : 4}
          />
        </div>
      }
    />
  );
}
